import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getDepartments, createDepartment, getDesignations, createDesignation } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Building2, Briefcase, Plus } from 'lucide-react';
import { toast } from 'sonner';

const Departments = ({ user, setUser }) => {
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [desigDialogOpen, setDesigDialogOpen] = useState(false);
  const [deptForm, setDeptForm] = useState({ name: '', description: '' });
  const [desigForm, setDesigForm] = useState({ title: '', department_id: '', description: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [deptRes, desigRes] = await Promise.all([
        getDepartments(),
        getDesignations()
      ]);
      setDepartments(deptRes.data);
      setDesignations(desigRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDepartmentSubmit = async (e) => {
    e.preventDefault();
    try {
      await createDepartment(deptForm);
      toast.success('Department created successfully');
      setDeptDialogOpen(false);
      setDeptForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create department');
    }
  };

  const handleDesignationSubmit = async (e) => {
    e.preventDefault();
    try {
      await createDesignation(desigForm);
      toast.success('Designation created successfully');
      setDesigDialogOpen(false);
      setDesigForm({ title: '', department_id: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create designation');
    }
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || 'N/A';
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="departments-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">Organization Structure</h1>
          <p className="text-slate-600 mt-1">Manage departments and designations</p>
        </div>

        <Tabs defaultValue="departments" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="departments" data-testid="departments-tab">Departments</TabsTrigger>
            <TabsTrigger value="designations" data-testid="designations-tab">Designations</TabsTrigger>
          </TabsList>

          <TabsContent value="departments" className="space-y-6" data-testid="departments-content">
            <div className="flex justify-end">
              <Dialog open={deptDialogOpen} onOpenChange={setDeptDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full" data-testid="add-department-btn">
                    <Plus className="mr-2" size={20} />
                    Add Department
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-department-dialog">
                  <DialogHeader>
                    <DialogTitle>Add New Department</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleDepartmentSubmit} className="space-y-4" data-testid="department-form">
                    <div className="space-y-2">
                      <Label htmlFor="dept_name">Department Name *</Label>
                      <Input
                        id="dept_name"
                        value={deptForm.name}
                        onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                        required
                        data-testid="dept-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dept_description">Description</Label>
                      <Textarea
                        id="dept_description"
                        value={deptForm.description}
                        onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })}
                        data-testid="dept-description-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="submit-department-btn">
                      Create Department
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {departments.length === 0 ? (
                <div className="col-span-3 text-center py-12">
                  <p className="text-slate-500">No departments found. Create your first department!</p>
                </div>
              ) : (
                departments.map((dept, index) => (
                  <Card key={dept.id} className="border border-slate-100 shadow-sm card-hover" data-testid={`department-card-${index}`}>
                    <CardHeader className="border-b border-slate-50">
                      <CardTitle className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center">
                          <Building2 size={24} />
                        </div>
                        <span data-testid={`department-name-${index}`}>{dept.name}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <p className="text-sm text-slate-600">{dept.description || 'No description'}</p>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="designations" className="space-y-6" data-testid="designations-content">
            <div className="flex justify-end">
              <Dialog open={desigDialogOpen} onOpenChange={setDesigDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full" data-testid="add-designation-btn">
                    <Plus className="mr-2" size={20} />
                    Add Designation
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-designation-dialog">
                  <DialogHeader>
                    <DialogTitle>Add New Designation</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleDesignationSubmit} className="space-y-4" data-testid="designation-form">
                    <div className="space-y-2">
                      <Label htmlFor="desig_title">Title *</Label>
                      <Input
                        id="desig_title"
                        value={desigForm.title}
                        onChange={(e) => setDesigForm({ ...desigForm, title: e.target.value })}
                        required
                        data-testid="desig-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="desig_dept">Department *</Label>
                      <Select
                        value={desigForm.department_id}
                        onValueChange={(value) => setDesigForm({ ...desigForm, department_id: value })}
                        required
                      >
                        <SelectTrigger data-testid="desig-dept-select">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          {departments.map(dept => (
                            <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="desig_description">Description</Label>
                      <Textarea
                        id="desig_description"
                        value={desigForm.description}
                        onChange={(e) => setDesigForm({ ...desigForm, description: e.target.value })}
                        data-testid="desig-description-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="submit-designation-btn">
                      Create Designation
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {designations.length === 0 ? (
                <div className="col-span-3 text-center py-12">
                  <p className="text-slate-500">No designations found. Create your first designation!</p>
                </div>
              ) : (
                designations.map((desig, index) => (
                  <Card key={desig.id} className="border border-slate-100 shadow-sm card-hover" data-testid={`designation-card-${index}`}>
                    <CardHeader className="border-b border-slate-50">
                      <CardTitle className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center">
                          <Briefcase size={24} />
                        </div>
                        <span data-testid={`designation-title-${index}`}>{desig.title}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 space-y-2">
                      <p className="text-sm text-slate-600">{desig.description || 'No description'}</p>
                      <div className="pt-2 border-t border-slate-100">
                        <p className="text-xs text-slate-500">Department: {getDepartmentName(desig.department_id)}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Departments;